import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { RotateCcw } from "lucide-react";
import { toast } from "sonner";

const CONFETTI = ["#E30B5C", "#FFB6C1", "#FFD700", "#FF69B4", "#ffffff"];
const CANDLE_X = [112, 150, 188];

export default function CakeSection() {
  const [lit, setLit] = useState([true, true, true]);
  const [wished, setWished] = useState(false);

  const blowOut = (i) => setLit((prev) => prev.map((v, idx) => (idx === i ? false : v)));
  const relight = () => {
    setLit([true, true, true]);
    setWished(false);
  };

  useEffect(() => {
    if (lit.every((v) => !v) && !wished) {
      setWished(true);
      confetti({
        particleCount: 160,
        spread: 120,
        origin: { y: 0.6 },
        colors: CONFETTI,
        scalar: 1.15,
        ticks: 240,
      });
      toast.success("Your wish is on its way to the stars!");
    }
  }, [lit, wished]);

  return (
    <section className="relative z-10 px-6 py-20 sm:py-28" data-testid="cake-section">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        className="max-w-2xl mx-auto rounded-3xl border border-white/40 bg-white/60 backdrop-blur-2xl backdrop-saturate-150 p-6 sm:p-10 shadow-[0_8px_32px_rgba(227,11,92,0.15)] text-center"
      >
        <h2
          className="text-4xl sm:text-5xl text-[#2C1820] mt-3"
          style={{ fontFamily: "'Caveat', cursive" }}
        >
          Gen1
        </h2>
        <p className="mt-4 text-[#5C404C] text-base sm:text-lg">
          Tap each flame to put out the candles!
        </p>

        <div className="relative mx-auto mt-10 h-[280px] w-[300px]" data-testid="birthday-cake">
          {CANDLE_X.map((x, i) => (
            <div key={i} className="absolute" style={{ left: x, bottom: 150, width: 10 }}>
              <div
                className="h-10 w-full rounded-sm"
                style={{
                  background: "repeating-linear-gradient(45deg,#E30B5C 0 4px,#ffffff 4px 8px)",
                }}
              />
              <AnimatePresence>
                {lit[i] ? (
                  <motion.button
                    key="flame"
                    data-testid={`candle-flame-${i}`}
                    aria-label={`Put out candle ${i + 1}`}
                    onClick={() => blowOut(i)}
                    className="absolute -top-8 left-1/2 h-7 w-4 cursor-pointer"
                    style={{ x: "-50%" }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                  >
                    <motion.div
                      className="h-full w-full"
                      style={{
                        borderRadius: "50% 50% 50% 50% / 62% 62% 38% 38%",
                        background:
                          "radial-gradient(circle at 50% 72%, #FFF3B0 0%, #FFD166 45%, #FF9F1C 75%, #FF6B00 100%)",
                        boxShadow: "0 0 18px 6px rgba(255,180,60,0.55)",
                      }}
                      animate={{
                        scaleY: [1, 1.22, 0.92, 1.12, 1],
                        scaleX: [1, 0.88, 1.08, 0.95, 1],
                        rotate: [0, -3, 3, -2, 0],
                      }}
                      transition={{ duration: 0.9, repeat: Infinity, ease: "easeInOut" }}
                    />
                  </motion.button>
                ) : (
                  <motion.span
                    key="smoke"
                    initial={{ opacity: 0.7, y: 0, scale: 0.5 }}
                    animate={{ opacity: 0, y: -34, scale: 1.5 }}
                    transition={{ duration: 1.2, ease: "easeOut" }}
                    className="absolute -top-6 left-1/2 h-3 w-3 rounded-full bg-gray-400/70"
                    style={{ x: "-50%" }}
                  />
                )}
              </AnimatePresence>
            </div>
          ))}

          <div className="absolute bottom-[66px] left-1/2 w-44 h-[86px] -translate-x-1/2 rounded-t-2xl bg-[#FFF0F5] border border-[#FFD6E0]">
            <div className="absolute top-0 left-0 right-0 h-5 rounded-t-2xl bg-[#FF8FA3]" />
            <div className="absolute top-4 left-3 h-4 w-3 rounded-b-full bg-[#FF8FA3]" />
            <div className="absolute top-4 left-1/2 -translate-x-1/2 h-6 w-3 rounded-b-full bg-[#FF8FA3]" />
            <div className="absolute top-4 right-3 h-4 w-3 rounded-b-full bg-[#FF8FA3]" />
          </div>

          <div className="absolute bottom-4 left-1/2 w-60 h-[64px] -translate-x-1/2 rounded-t-2xl bg-[#FFB6C1]">
            <div className="absolute top-0 left-0 right-0 h-5 rounded-t-2xl bg-white/90" />
            <div className="absolute top-6 left-6 h-1.5 w-1.5 rounded-full bg-[#E30B5C]" />
            <div className="absolute top-9 left-16 h-1.5 w-1.5 rounded-full bg-[#FFD700]" />
            <div className="absolute top-7 left-28 h-1.5 w-1.5 rounded-full bg-[#C084FC]" />
            <div className="absolute top-10 right-14 h-1.5 w-1.5 rounded-full bg-[#E30B5C]" />
            <div className="absolute top-6 right-6 h-1.5 w-1.5 rounded-full bg-[#FFD700]" />
          </div>

          <div className="absolute bottom-0 left-1/2 h-5 w-[300px] -translate-x-1/2 rounded-full bg-[#F3D9E4] shadow-md" />
        </div>

        <div className="mt-8 flex items-center justify-center gap-4 flex-wrap">
          {wished && (
            <button
              data-testid="relight-button"
              onClick={relight}
              className="rounded-full border-2 border-[#E30B5C] text-[#E30B5C] font-semibold px-8 py-3.5 flex items-center gap-2 hover:bg-[#E30B5C] hover:text-white hover:-translate-y-1 transition-[background-color,color,transform] duration-300 ease-out"
            >
              <RotateCcw className="w-4 h-4" /> Relight candles
            </button>
          )}
        </div>

        <AnimatePresence>
          {wished && (
            <motion.p
              data-testid="wish-message"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mt-6 text-3xl text-[#E30B5C]"
              style={{ fontFamily: "'Caveat', cursive" }}
            >
              May every wish you make tonight come true
            </motion.p>
          )}
        </AnimatePresence>
      </motion.div>
    </section>
  );
}
